// RPTCAT0: renders the server's fixed rendition. No source query, totals, ratios or quantity conversions.
import {
  cannedFamily,
  cellText,
  flat,
  label,
  record,
  reportWords,
  type CannedResultDto,
} from "./cannedReportModel.ts";
import { hoverHelp } from "./hoverHelp.ts";

export function reportNode<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  return element;
}
function dataTable(rows: Record<string, unknown>[], caption: string): HTMLElement {
  const wrap = reportNode("div");
  wrap.className = "canned-table-scroll";
  wrap.tabIndex = 0;
  const table = reportNode("table");
  table.append(reportNode("caption", caption));
  const cells = rows.map((row) => flat(row)),
    keys = [...new Set(cells.flatMap((row) => Object.keys(row)))];
  const head = reportNode("thead"),
    header = reportNode("tr");
  for (const key of keys) {
    const th = reportNode("th", label(key));
    th.scope = "col";
    header.append(th);
  }
  head.append(header);
  table.append(head);
  const body = reportNode("tbody");
  for (const row of cells) {
    const tr = reportNode("tr");
    for (const key of keys) tr.append(reportNode("td", cellText(row[key], key)));
    body.append(tr);
  }
  table.append(body);
  wrap.append(table);
  if (rows.length === 0) wrap.append(reportNode("p", "No recorded rows in this scope."));
  return wrap;
}
function vendorSpendMetrics(value: unknown): Record<string, unknown> | null {
  if (!record(value)) return null;
  const keys = [
    "deliveredSubtotalCents",
    "correctedSubtotalCents",
    "correctionSubtotalCents",
    "completeCorrectedTotalCents",
    "receiptLineCount",
    "pricedLineCount",
    "unpricedLineCount",
    "coverage",
  ] as const;
  return Object.fromEntries(keys.map((key) => [key, value[key]]));
}
function networkOpenOrderCounts(value: unknown): Record<string, unknown> | null {
  if (!record(value)) return null;
  const fields = [
    "orderCount",
    "draftCount",
    "placedCount",
    "partiallyReceivedCount",
    "exceptionCount",
    "contributingLineCount",
  ] as const;
  return Object.fromEntries(fields.map((field) => [field, value[field]]));
}
function facts(value: unknown): string {
  if (!record(value)) return typeof value === "string" ? reportWords(value) : "";
  return Object.entries(flat(value))
    .map(
      ([key, cell]) =>
        `${label(key)}: ${typeof cell === "string" ? reportWords(cell) : cellText(cell, key)}`,
    )
    .join(". ");
}
const chartFields: Readonly<Record<string, readonly string[]>> = {
  "sales-summary": ["current", "prior"],
  "daily-sales": ["current", "prior"],
  "sales-by-weekday": ["current", "prior"],
  "location-comparison": ["current", "prior"],
  "product-mix": ["current", "prior"],
  "category-share": ["current", "prior"],
  "weekly-item-movement": ["current", "prior"],
  "royalty-basis": [
    "latestStatementRoyaltyCents",
    "currentlySucceededCollectedCents",
    "priorLatestStatementRoyaltyCents",
    "priorCurrentlySucceededCollectedCents",
  ],
  "current-inventory-value": ["valueCents"],
  "closed-count-session-summary": [
    "sessionCount",
    "countedProductCount",
    "adjustmentProductCount",
    "emptyCloseCount",
  ],
  "received-delivery-cost": ["currentCents", "priorCents"],
  "received-spend-by-vendor": ["current", "prior"],
  "prepared-voids": ["voidEventCount"],
  "discount-applications": ["applicationEventCount"],
  "recorded-refunds": ["recordedAmountCents", "refundRecordCount"],
  "paid-in-out": ["paidInCents", "paidOutCents", "netMovementCents", "movementEventCount"],
  walkouts: ["walkoutCount", "walkoutOwedCents"],
  "price-overrides": ["priceChangeEventCount", "recordedLinePriceDeltaCents"],
  "payments-by-method": ["grossCollectedCents"],
  "loss-prevention-trends": ["value"],
  "tips-by-employee-day": ["recordedTipsCents"],
  "employee-activity": ["recordedPreparationQuantity"],
  "guest-ticket-activity": ["guestTicketAssociations"],
  "waste-reason-activity": ["wasteMovementCount", "wasteReversalCount", "netEventContribution"],
};
function chartPanel(
  chart: Record<string, unknown>,
  family: string,
  title: string,
  selectedCategory = false,
): HTMLElement {
  const root = reportNode("section");
  root.className = "canned-chart";
  root.append(reportNode("h3", title));
  if (
    chart["availability"] === "unavailable" ||
    chart["truncated"] === true ||
    !Array.isArray(chart["points"]) ||
    chart["points"].length === 0
  ) {
    root.append(
      reportNode(
        "p",
        chart["truncated"] === true
          ? "Choose a smaller scope for a complete chart. Full totals and CSV remain available."
          : "Chart unavailable for this result.",
      ),
    );
    return root;
  }
  const points = chart["points"].filter(record);
  const shareChart = chart["type"] === "share" || chart["type"] === "pie";
  const candidates = shareChart
    ? (family === "received-spend-by-vendor" ||
        (family === "category-share" && selectedCategory)) &&
      points.some((point) => point["prior"] !== null)
      ? ["current", "prior"]
      : ["current"]
    : family === "preparation-stock-movements"
      ? ["depletionQuantity", "returnedQuantity", "netDepletionQuantity"]
      : (chartFields[family] ?? []);
  // Select one actual measure at a time. Unlike units are never combined onto one axis.
  const fields = candidates.filter((key) => points.some((point) => point[key] !== undefined));
  if (fields.length === 0) {
    root.append(reportNode("p", "Chart unavailable for this result."));
    return root;
  }
  const picker = reportNode("select");
  picker.setAttribute("aria-label", "Chart measure");
  for (const key of fields) picker.append(new Option(label(key), key));
  root.append(picker);
  const picture = reportNode("div");
  picture.className = "canned-chart-picture";
  root.append(picture);
  const draw = () => {
    picture.replaceChildren();
    const field = picker.value;
    const values = points.map((point) => {
      const v = point[field];
      return typeof v === "number" && Number.isFinite(v)
        ? v
        : typeof v === "string" && /^-?\d+(?:\.\d{1,4})?$/.test(v)
          ? Number(v)
          : null;
    });
    const numeric = values.filter((v): v is number => v !== null && Number.isFinite(v));
    if (numeric.length === 0) {
      picture.append(reportNode("p", "No figures to chart for this report."));
      return;
    }
    const width = Math.max(720, points.length * 36),
      height = 280,
      left = 64,
      bottom = 220,
      min = Math.min(0, ...numeric),
      max = Math.max(0, ...numeric),
      span = max - min || 1;
    const y = (v: number) => 20 + ((max - v) / span) * 180,
      x = (i: number) => left + ((i + 0.5) * (width - left - 16)) / points.length;
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute(
      "viewBox",
      `0 0 ${width} ${shareChart ? Math.max(height, points.length * 22 + 40) : height}`,
    );
    svg.setAttribute("width", String(width));
    svg.setAttribute(
      "height",
      String(shareChart ? Math.max(height, points.length * 22 + 40) : height),
    );
    svg.setAttribute("role", "img");
    svg.setAttribute("aria-label", `${title}: ${label(field)}. ${points.length} recorded points.`);
    const mark = (tag: string, attrs: Record<string, string>, text?: string) => {
      const element = document.createElementNS(svg.namespaceURI, tag);
      for (const [k, v] of Object.entries(attrs)) element.setAttribute(k, v);
      if (text !== undefined) element.textContent = text;
      svg.append(element);
      return element;
    };
    const money =
      chart["unit"] === "USD_cents" || /cents/i.test(field) || ["current", "prior"].includes(field);
    const format = (v: number) => cellText(v, money ? "cents" : field);
    if (
      shareChart &&
      (field === "current" ||
        ((family === "received-spend-by-vendor" ||
          (family === "category-share" && selectedCategory)) &&
          field === "prior"))
    ) {
      // Shares are supplied by the fixed server adapter, never recomputed from displayed rows.
      let angle = -Math.PI / 2;
      let hasSlices = false;
      for (let i = 0; i < points.length; i++) {
        const share = points[i]?.[field === "prior" ? "priorShareBp" : "shareBp"];
        if (typeof share !== "number" || share <= 0) continue;
        hasSlices = true;
        const end = angle + (share / 10000) * Math.PI * 2,
          large = share > 5000 ? 1 : 0;
        if (share === 10000)
          mark("circle", { cx: "170", cy: "125", r: "95", class: `canned-series-${i % 6}` });
        else
          mark("path", {
            d: `M 170 125 L ${170 + 95 * Math.cos(angle)} ${125 + 95 * Math.sin(angle)} A 95 95 0 ${large} 1 ${170 + 95 * Math.cos(end)} ${125 + 95 * Math.sin(end)} Z`,
            class: `canned-series-${i % 6}`,
          });
        angle = end;
        const point = points[i]!;
        mark(
          "text",
          { x: "290", y: String(30 + i * 22) },
          `${String(point["label"] ?? (family === "received-spend-by-vendor" ? "Vendor name missing" : selectedCategory ? "Category missing" : (point["key"] ?? i + 1)))}: ${values[i] === null ? "-" : format(values[i]!)} (${share / 100}%)`,
        );
      }
      if (!hasSlices) {
        picture.append(reportNode("p", "No figures to chart for this report."));
        return;
      }
    } else {
      mark("line", {
        x1: String(left),
        x2: String(width - 12),
        y1: String(y(0)),
        y2: String(y(0)),
        class: "canned-axis",
      });
      mark("text", { x: "2", y: "18" }, format(max));
      mark("text", { x: "2", y: String(bottom) }, format(min));
      let previous: { x: number; y: number } | undefined;
      for (let i = 0; i < points.length; i++) {
        const point = points[i]!,
          v = values[i],
          px = x(i);
        if (v === null || v === undefined) {
          previous = undefined;
          continue;
        }
        const py = y(v),
          pointLabel = String(
            point["label"] ?? point["businessDate"] ?? point["date"] ?? point["month"] ?? i + 1,
          );
        const element =
          chart["type"] === "line"
            ? mark("circle", { cx: String(px), cy: String(py), r: "3", class: "canned-point" })
            : mark("rect", {
                x: String(px - 10),
                width: "20",
                y: String(Math.min(py, y(0))),
                height: String(Math.max(1, Math.abs(y(0) - py))),
                class: "canned-point",
              });
        const accessible = document.createElementNS(svg.namespaceURI, "title");
        accessible.textContent = `${pointLabel}: ${format(v)}`;
        element.append(accessible);
        if (chart["type"] === "line" && previous !== undefined)
          mark("line", {
            x1: String(previous.x),
            y1: String(previous.y),
            x2: String(px),
            y2: String(py),
            class: "canned-line",
          });
        previous = { x: px, y: py };
        mark(
          "text",
          { x: String(px), y: "240", transform: `rotate(35 ${px} 240)`, "font-size": "10" },
          pointLabel,
        );
      }
    }
    picture.append(svg);
  };
  picker.onchange = draw;
  draw();
  return root;
}
export function renderCannedResult(result: CannedResultDto, mode: "table" | "chart"): HTMLElement {
  const root = reportNode("div"),
    family = cannedFamily(result.template);
  if (family === undefined) {
    root.append(reportNode("p", "This report rendition is unavailable."));
    return root;
  }
  if (family === "network-current-open-purchase-orders") {
    if (
      result.scope.kind !== "network" ||
      !result.rows.every(
        (row) =>
          typeof row["locationId"] === "string" &&
          result.scope.locations.some((site) => site.id === row["locationId"]) &&
          typeof row["locationName"] === "string" &&
          typeof row["recordedVendorName"] === "string" &&
          typeof row["createdBusinessDate"] === "string" &&
          ["draft", "placed", "partially_received", "exception"].includes(String(row["status"])) &&
          Number.isSafeInteger(row["contributingLineCount"]) &&
          Number(row["contributingLineCount"]) >= 0,
      )
    ) {
      root.append(reportNode("p", "Network current open purchase orders are unavailable."));
      return root;
    }
  }
  if (family === "current-open-purchase-orders") {
    if (
      result.scope.kind !== "fixed_location" ||
      result.scope.locations.length !== 1 ||
      !result.rows.every(
        (row) =>
          typeof row["orderId"] === "string" &&
          typeof row["recordedVendorName"] === "string" &&
          typeof row["createdBusinessDate"] === "string" &&
          (row["placedBusinessDate"] === null || typeof row["placedBusinessDate"] === "string") &&
          ["draft", "placed", "partially_received", "exception"].includes(String(row["status"])) &&
          Number.isSafeInteger(row["contributingLineCount"]) &&
          Number(row["contributingLineCount"]) > 0,
      )
    ) {
      root.append(reportNode("p", "Current open purchase orders are unavailable."));
      return root;
    }
    root.append(
      reportNode(
        "p",
        `${result.scope.locations[0]!.name} · Created in selected period · Current open status`,
      ),
    );
  }
  if (family === "recorded-transfer-quantities") {
    const direction = result["direction"],
      anchorContext = result["anchor"];
    const columns = [
      "counterpartLocationId",
      "itemId",
      "recordedReference",
      "quantityUnit",
      "sentQuantity",
      "awaitingQuantity",
      "receivedQuantity",
      "shortQuantity",
      "cancelledSentQuantity",
      "contributingLineCount",
    ] as const;
    const quantities = [
      "sentQuantity",
      "awaitingQuantity",
      "receivedQuantity",
      "shortQuantity",
      "cancelledSentQuantity",
    ] as const;
    if (
      result.scope.kind !== "fixed_location" ||
      result.scope.locations.length !== 1 ||
      (direction !== "incoming" && direction !== "outgoing") ||
      !record(anchorContext) ||
      typeof anchorContext["id"] !== "string" ||
      typeof anchorContext["name"] !== "string" ||
      anchorContext["id"] !== result.scope.locations[0]?.id ||
      !result.rows.every(
        (row) =>
          columns.slice(0, 4).every((key) => typeof row[key] === "string") &&
          quantities.every(
            (key) => typeof row[key] === "string" && /^\d+(?:\.\d{1,4})?$/.test(row[key] as string),
          ) &&
          Number.isSafeInteger(row["contributingLineCount"]) &&
          Number(row["contributingLineCount"]) > 0,
      )
    ) {
      root.append(reportNode("p", "Recorded transfer quantities are unavailable."));
      return root;
    }
    root.append(reportNode("p", `${reportWords(direction)} · ${anchorContext["name"]}`));
  }
  const source = facts(result["source"]),
    coverage = facts(result["coverage"]);
  const meta = reportNode("div");
  meta.className = "canned-result-meta";
  meta.append(
    hoverHelp(
      reportNode("span", `Updated ${result.generatedAt}`),
      source || "Recorded report data only.",
      coverage || "Coverage is provided by the report source.",
      "Missing values show as a dash.",
    ),
  );
  if (record(result["range"])) meta.append(reportNode("span", facts(result["range"])));
  root.append(meta);
  if (
    record(result["categories"]) &&
    result["categories"]["kind"] === "named" &&
    typeof result["categories"]["value"] === "string" &&
    record(result["selection"])
  ) {
    root.append(reportNode("h3", result["categories"]["value"]));
    const selection = result["selection"];
    const keys = [
      "selectedAttributedNetCents",
      "excludedAttributedNetCents",
      "unallocatedNetCents",
      "fullScopeNetCents",
      "selectedQuantity",
      "excludedAttributedQuantity",
      "unallocatedContributionCount",
    ] as const;
    const projection = (value: unknown) =>
      record(value) ? Object.fromEntries(keys.map((key) => [key, value[key]])) : null;
    root.append(
      dataTable(
        [{ current: projection(selection["current"]), prior: projection(selection["prior"]) }],
        "Selected category and full scope coverage",
      ),
    );
  }
  if (record(result["session"]))
    root.append(dataTable([result["session"]], "Selected closed session"));
  if (record(result["sessions"]))
    root.append(dataTable([result["sessions"]], "Baseline and comparison sessions"));
  if (
    mode === "chart" &&
    ![
      "recorded-transfer-quantities",
      "current-open-purchase-orders",
      "network-current-open-purchase-orders",
    ].includes(family)
  ) {
    const chart = result["chart"];
    if (record(chart) && Array.isArray(chart["panels"]))
      for (const panel of chart["panels"]) {
        if (record(panel))
          root.append(
            chartPanel(
              { ...chart, ...panel },
              family,
              family === "category-share" &&
                record(result["categories"]) &&
                result["categories"]["kind"] === "named"
                ? String(panel["locationName"] ?? "Location")
                : `${String(panel["recordedReference"] ?? "Recorded item")} · ${String(panel["quantityUnit"] ?? "")}`,
              record(result["categories"]) && result["categories"]["kind"] === "named",
            ),
          );
      }
    else if (record(chart))
      root.append(
        chartPanel(
          chart,
          family,
          "Chart",
          record(result["categories"]) && result["categories"]["kind"] === "named",
        ),
      );
    else root.append(reportNode("p", "Chart unavailable for this report."));
  } else if (family === "recorded-transfer-quantities") {
    const columns = [
      "counterpartLocationId",
      "itemId",
      "recordedReference",
      "quantityUnit",
      "sentQuantity",
      "awaitingQuantity",
      "receivedQuantity",
      "shortQuantity",
      "cancelledSentQuantity",
      "contributingLineCount",
    ] as const;
    root.append(
      dataTable(
        result.rows.map((row) => Object.fromEntries(columns.map((key) => [key, row[key]]))),
        "Recorded transfer quantities",
      ),
    );
  } else if (family === "received-spend-by-vendor") {
    root.append(
      dataTable(
        result.rows.map((row) => ({
          locationName: row["locationName"],
          ...(typeof row["franchisee"] === "string" ? { franchisee: row["franchisee"] } : {}),
          vendorLabel: row["vendorLabel"],
          current: vendorSpendMetrics(row["current"]),
          prior: row["prior"] === null ? null : vendorSpendMetrics(row["prior"]),
          correctedChangeBp: row["correctedChangeBp"],
        })),
        "Recorded received spend by vendor",
      ),
    );
  } else if (family === "network-current-open-purchase-orders") {
    root.append(
      dataTable(
        result.rows.map((row) => ({
          locationName: row["locationName"],
          ...(typeof row["franchisee"] === "string" ? { franchisee: row["franchisee"] } : {}),
          recordedVendorName: row["recordedVendorName"],
          createdBusinessDate: row["createdBusinessDate"],
          status: reportWords(String(row["status"])),
          contributingLineCount: row["contributingLineCount"],
        })),
        "Network current open purchase orders",
      ),
    );
  } else if (family === "current-open-purchase-orders") {
    const columns = [
      "orderId",
      "recordedVendorName",
      "createdBusinessDate",
      "placedBusinessDate",
      "status",
      "contributingLineCount",
    ] as const;
    root.append(
      dataTable(
        result.rows.map((row) =>
          Object.fromEntries(
            columns.map((key) => [
              key,
              key === "status" ? reportWords(String(row[key])) : row[key],
            ]),
          ),
        ),
        "Current open purchase orders",
      ),
    );
  } else
    root.append(
      dataTable(
        record(result["categories"]) && result["categories"]["kind"] === "named"
          ? result.rows.map((row) =>
              Object.fromEntries(
                Object.entries(row).filter(
                  ([key]) => !["key", "locationId", "itemKey"].includes(key),
                ),
              ),
            )
          : result.rows,
        "Recorded detail",
      ),
    );
  if (result.display.truncated)
    root.append(
      reportNode("p", "The table is limited. CSV contains the complete authorized result."),
    );
  // Only server supplied summaries. Count-item and comparison technical control totals are never displayed.
  if (
    ![
      "closed-count-item-details",
      "closed-count-comparison",
      "recorded-transfer-quantities",
      "current-open-purchase-orders",
      "network-current-open-purchase-orders",
      "received-spend-by-vendor",
    ].includes(family)
  )
    for (const key of ["totals", "summary"]) {
      if (record(result[key]))
        root.append(dataTable([result[key]], key === "totals" ? "Full scope totals" : "Summary"));
    }
  if (family === "received-spend-by-vendor" && record(result["totals"])) {
    const totals = result["totals"];
    root.append(
      dataTable(
        [
          {
            current: vendorSpendMetrics(totals["current"]),
            prior: totals["prior"] === null ? null : vendorSpendMetrics(totals["prior"]),
            correctedChangeBp: totals["correctedChangeBp"],
          },
        ],
        "Full scope received spend and price coverage",
      ),
    );
  }
  if (family === "network-current-open-purchase-orders") {
    if (record(result["summary"]))
      root.append(
        dataTable([networkOpenOrderCounts(result["summary"])!], "Full scope open-order counts"),
      );
    if (Array.isArray(result["locationSummaries"]) && result["locationSummaries"].every(record)) {
      const byLocation = reportNode("details");
      byLocation.append(
        reportNode("summary", "Counts by location"),
        dataTable(
          result["locationSummaries"].map((row) => ({
            locationName: row["locationName"],
            ...(typeof row["franchisee"] === "string" ? { franchisee: row["franchisee"] } : {}),
            summary: networkOpenOrderCounts(row["summary"]),
          })),
          "Counts by location",
        ),
      );
      root.append(byLocation);
    }
  }
  if (family === "current-open-purchase-orders" && record(result["summary"])) {
    const summary = result["summary"];
    const counts = [
      "orderCount",
      "draftCount",
      "placedCount",
      "partiallyReceivedCount",
      "exceptionCount",
      "contributingLineCount",
    ] as const;
    root.append(
      dataTable(
        [Object.fromEntries(counts.map((key) => [key, summary[key]]))],
        "Current open-order counts",
      ),
    );
  }
  if (
    family === "recorded-transfer-quantities" &&
    record(result["summary"]) &&
    Number.isSafeInteger(result["summary"]["contributingLineCount"])
  )
    root.append(
      dataTable(
        [{ contributingLineCount: result["summary"]["contributingLineCount"] }],
        "Contributing lines",
      ),
    );
  for (const key of [
    "recorded-transfer-quantities",
    "current-open-purchase-orders",
    "network-current-open-purchase-orders",
  ].includes(family)
    ? []
    : ["locations", "employees", "days", "outcomes", "kinds"]) {
    const rows = result[key];
    if (Array.isArray(rows) && rows.length > 0 && rows.every(record)) {
      const disclosure = reportNode("details");
      disclosure.append(reportNode("summary", reportWords(key)), dataTable(rows, reportWords(key)));
      root.append(disclosure);
    }
  }
  return root;
}
